package uk.co.nickthecoder.kyd.shapes

import uk.co.nickthecoder.kyd.util.Vector2
import kotlin.math.*

private const val miterMinAngle: Float = 0.349066f
private const val roundMinAngle: Float = 0.174533f


class StrokeMesh : StrokeBuilder() {

    var includeStrokeGradient = false
    var includeTravel = false

    protected val data = mutableListOf<Float>()

    fun createStrokeMesh(points: List<Vector2>, isClosed: Boolean): Mesh {
        StrokeMeshWorker().stroke(points, isClosed)
        var floatsPerVertex = 2
        if (includeStrokeGradient) floatsPerVertex ++

        return Mesh(MeshBuffer(data), isTriangleStrip = false, floatsPerVertex = floatsPerVertex)
        // length = totalLength, thickness = thickness)(/
    }

    private inner class StrokeMeshWorker : Worker() {

        override fun addMeshPoint(point: Vector2, distance: Float, isOutside: Boolean) {
            data.add(point.x)
            data.add(point.y)
            if (includeStrokeGradient) data.add(if (isOutside) 1f else 0f)
            if (includeTravel) data.add(distance)
        }

        override fun addMeshEndCapCenterPoint(point: Vector2, distance: Float) {
            data.add(point.x)
            data.add(point.y)
            if (includeStrokeGradient) data.add(0.5f)
            if (includeTravel) data.add(distance)
        }

        override fun addMeshEndCapFanPoint(point: Vector2, distance: Float, fanNumber: Int, fanCount: Int) {
            data.add(point.x)
            data.add(point.y)

            if (includeStrokeGradient) {
                val angle = PI * (fanNumber / fanCount.toDouble() - 0.5)
                val u = sin(angle).toFloat() / -2 + 0.5f
                data.add(u)
            }
            if (includeTravel) data.add(distance)
        }
    }

}

/**
 * Used in 3 different ways :
 *
 * 1. To create meshes for a [Polygon]'s stroke. (See [StrokeMesh])
 * 2. To create meshes for a [Line]'s fill. (See [StrokeMesh])
 * 3. Takes a [Line] and a line-width ([ShapeAppearance.lineWidth]), and produces a "polygon"
 * (a list of [Vector2]).
 * See [ThickenLine].
 * This "polygon" is then filled and stroked in the same way as a regular [Polygon].
 *
 */
abstract class StrokeBuilder {

    var capStyle: CapStyle = CapStyle.ROUND
    var joinStyle: JoinStyle = JoinStyle.ROUND
    var insideThickness = 1f
    var outsideThickness = 1f

    /**
     *
     */
    protected abstract inner class Worker {

        // Used when creating meshes
        abstract fun addMeshPoint(point: Vector2, distance: Float, isOutside: Boolean)
        abstract fun addMeshEndCapFanPoint(point: Vector2, distance: Float, fanNumber: Int, fanCount: Int)
        abstract fun addMeshEndCapCenterPoint(point: Vector2, distance: Float)

        // Used when creating an outline of a Line.
        open fun addPair(inside: Vector2, outside: Vector2) {}
        open fun addInteriorPoint(point: Vector2, outOfSequence: Boolean = false) {}
        open fun addExteriorPoint(point: Vector2, outOfSequence: Boolean = false) {}
        open fun addEndCapPoint(point: Vector2, isStart: Boolean) {}

        private var allowOverlap = true

        private var end1 = Vector2.ZERO
        private var end2 = Vector2.ZERO
        private var nextStart1 = Vector2.ZERO
        private var nextStart2 = Vector2.ZERO
        private var start1 = Vector2.ZERO
        private var start2 = Vector2.ZERO

        fun stroke(points: List<Vector2>, isClosed: Boolean, calculateDistance: Boolean = false) {

            // We must get the order of the points correct when insideThickness != outsideThickness.
            val vertices = if (isClosed && isClockwise(points)) {
                points
            } else {
                points.asReversed()
            }

            var totalLength = 0f
            if (calculateDistance) {
                for (i in 1 until vertices.size) {
                    totalLength += (vertices[i] - vertices[i - 1]).magnitude()
                }
                if (isClosed) totalLength += (vertices.first() - vertices.last()).magnitude()
                if (capStyle != CapStyle.BUTT) totalLength += (insideThickness + outsideThickness) / 2
            } else {
                totalLength = 1f
            }

            val endCapTravel =
                if (capStyle == CapStyle.BUTT) 0f else (insideThickness + outsideThickness) / 2 / totalLength

            val segments = mutableListOf<PolySegment>()
            for (i in 0 until vertices.size - 1) {
                val point1 = vertices[i]
                val point2 = vertices[i + 1]

                // to avoid division-by-zero errors,
                // only create a line segment for non-identical points
                if (point1 != point2) {
                    segments.add(PolySegment(LineSegment(point1, point2), insideThickness, outsideThickness))
                }
            }

            if (segments.isEmpty()) {
                // handle the case of insufficient input points
                return
            }

            if (isClosed) {
                // create a connecting segment from the last to the first point

                // to avoid division-by-zero errors, only create a line segment for non-identical points
                if (vertices.first() != vertices.last()) {
                    segments.add(
                        PolySegment(
                            LineSegment(vertices.last(), vertices.first()),
                            insideThickness,
                            outsideThickness
                        )
                    )
                }
            }


            val firstSegment = segments.first()
            val lastSegment = segments.last()
            var pathStart1 = firstSegment.edge1.start
            var pathStart2 = firstSegment.edge2.start
            var pathEnd1: Vector2 = lastSegment.edge1.end
            var pathEnd2: Vector2 = lastSegment.edge2.end

            if (isClosed) {
                createJoint(lastSegment, firstSegment, 1f)
                // createJoint only updates end1, end2, nextStart1 and nextStart2.
                // The original didn't need this extra code.
                if (joinStyle != JoinStyle.ROUND) {
                    pathStart1 = nextStart1
                    pathStart2 = nextStart2
                    pathEnd1 = end1
                    pathEnd2 = end2

                    firstSegment.edge1.start = nextStart1
                    firstSegment.edge2.start = nextStart2
                    lastSegment.edge1.end = end1
                    lastSegment.edge2.end = end2
                }
            } else {
                when (capStyle) {
                    CapStyle.SQUARE -> {
                        // extend the start/end points by half the thickness
                        pathStart1 -= firstSegment.edge1.normalisedDirection() * outsideThickness
                        pathStart2 -= firstSegment.edge2.normalisedDirection() * insideThickness
                        pathEnd1 += lastSegment.edge1.normalisedDirection() * outsideThickness
                        pathEnd2 += lastSegment.edge2.normalisedDirection() * insideThickness
                    }

                    CapStyle.ROUND -> {
                        // Draw half circle end caps.
                        // Note, isEndCap=false when symmetric==true. This causes a circular gradient at the ends
                        // (which only looks nice when the gradient is made symmetrical).
                        createTriangleFan(
                            firstSegment.center.start, firstSegment.center.start,
                            firstSegment.edge1.start, firstSegment.edge2.start, false,
                            0f, true
                        )
                        createTriangleFan(
                            lastSegment.center.end, lastSegment.center.end,
                            lastSegment.edge1.end, lastSegment.edge2.end, true,
                            1f, true
                        )
                    }

                    CapStyle.BUTT -> {
                        // Do nothing.
                    }
                }
            }

            var travelled = 0f

            addPair(pathStart1, pathStart2)

            for (i in 0 until segments.size) {
                val segment = segments[i]

                val segmentLength = if (calculateDistance) segment.center.length() else 0f
                var nextTravelled = travelled + segmentLength / totalLength

                if (i == 0) {
                    start1 = pathStart1
                    start2 = pathStart2
                    if (capStyle != CapStyle.BUTT) {
                        travelled += endCapTravel
                        nextTravelled += endCapTravel
                    }
                }

                if (i + 1 == segments.size) {
                    // this is the last segment
                    end1 = pathEnd1
                    end2 = pathEnd2

                } else {
                    createJoint(segment, segments[i + 1], nextTravelled)
                }

                // The two triangles for the line of segment.
                addMeshPoint(start1, travelled, true)
                addMeshPoint(start2, travelled, false)
                addMeshPoint(end1, nextTravelled, true)

                addMeshPoint(end1, nextTravelled, true)
                addMeshPoint(start2, travelled, false)
                addMeshPoint(end2, nextTravelled, false)

                addPair(end1, end2)

                start1 = nextStart1
                start2 = nextStart2
                travelled = nextTravelled
            }

        }

        private fun createTriangleFan(
            connectTo: Vector2, origin: Vector2,
            start: Vector2, end: Vector2, clockwise: Boolean,
            travelledFrom: Float, isEndCap: Boolean
        ) {

            val point1 = start - origin
            val point2 = end - origin

            // calculate the angle between the two points
            var angle1 = atan2(point1.y.toDouble(), point1.x.toDouble())
            var angle2 = atan2(point2.y.toDouble(), point2.x.toDouble())

            // ensure the outer angle is calculated
            if (clockwise) {
                if (angle2 > angle1) {
                    angle2 -= 2 * PI
                }
            } else {
                if (angle1 > angle2) {
                    angle1 -= 2 * PI
                }
            }

            val jointAngle = angle2 - angle1

            // calculate the amount of triangles to use for the joint
            val numTriangles = max(1, floor(abs(jointAngle) / roundMinAngle).toInt())

            // calculate the angle of each triangle
            val triAngle = jointAngle / numTriangles

            var startPoint = start
            for (t in 1..numTriangles) {
                val rot = t * triAngle

                val cos = cos(rot).toFloat()
                val sin = sin(rot).toFloat()

                val endPoint = if (t == numTriangles) {
                    // it's the last triangle - ensure it perfectly connects to the next line
                    end
                } else {
                    // re-add the rotation origin to the target point
                    origin.plus(
                        (cos * point1.x - sin * point1.y),
                        (sin * point1.x + cos * point1.y)
                    )
                }

                // Add the triangle
                if (isEndCap) {
                    addMeshEndCapFanPoint(startPoint, travelledFrom, t - 1, numTriangles)
                    addMeshEndCapFanPoint(endPoint, travelledFrom, t, numTriangles)
                    addMeshEndCapCenterPoint(connectTo, travelledFrom)
                } else {
                    addMeshPoint(startPoint, travelledFrom, clockwise)
                    addMeshPoint(endPoint, travelledFrom, clockwise)
                    addMeshPoint(connectTo, travelledFrom, ! clockwise)
                }

                if (isEndCap) {
                    addEndCapPoint(endPoint, travelledFrom == 0f)
                } else {
                    if (clockwise) {
                        addInteriorPoint(endPoint, outOfSequence = true)
                    } else {
                        addExteriorPoint(endPoint, outOfSequence = true)
                    }
                }

                startPoint = endPoint
            }
        }

        private fun createJoint(segment1: PolySegment, segment2: PolySegment, travelled: Float) {

            // calculate the angle between the two line segments
            val dir1 = segment1.center.direction()
            val dir2 = segment2.center.direction()

            val angle = dir1.angleRadians(dir2)
            // wrap the angle around the 180° mark if it exceeds 90° for minimum angle detection
            var wrappedAngle = angle
            if (wrappedAngle > PI / 2) {
                wrappedAngle = PI.toFloat() - wrappedAngle
            }

            val actualJoinStyle = if (joinStyle == JoinStyle.MITER && wrappedAngle < miterMinAngle) {
                // The joint is too pointy (the angle is too small).
                // To avoid the intersection point being extremely far out, we render the joint beveled instead.
                JoinStyle.BEVEL
            } else {
                joinStyle
            }
            when (actualJoinStyle) {

                JoinStyle.MITER -> {
                    // calculate both edges' intersection point with the next segment's edges
                    val sec1 = LineSegment.intersection(segment1.edge1, segment2.edge1, true)
                    val sec2 = LineSegment.intersection(segment1.edge2, segment2.edge2, true)

                    end1 = sec1 ?: segment1.edge1.end
                    end2 = sec2 ?: segment1.edge2.end

                    nextStart1 = end1
                    nextStart2 = end2
                }

                JoinStyle.BEVEL, JoinStyle.ROUND -> {

                    // find out which are the inner edges for this joint
                    val x1 = dir1.x
                    val x2 = dir2.x
                    val y1 = dir1.y
                    val y2 = dir2.y

                    val clockwise = x1 * y2 - x2 * y1 < 0

                    val inner1: LineSegment
                    val inner2: LineSegment
                    val outer1: LineSegment
                    val outer2: LineSegment

                    // as the normal vector is rotated counter-clockwise, the first edge lies to the left
                    // from the central line's perspective, and the second one to the right.
                    if (clockwise) {
                        outer1 = segment1.edge1
                        outer2 = segment2.edge1
                        inner1 = segment1.edge2
                        inner2 = segment2.edge2
                    } else {
                        outer1 = segment1.edge2
                        outer2 = segment2.edge2
                        inner1 = segment1.edge1
                        inner2 = segment2.edge1
                    }

                    // calculate the intersection point of the inner edges
                    val innerSecOpt = LineSegment.intersection(inner1, inner2, allowOverlap)

                    // for parallel lines, simply connect them directly
                    val innerSec = innerSecOpt ?: inner1.end

                    // if there's no inner intersection, flip the next start position for near-180° turns
                    val innerStart = if (innerSecOpt != null) {
                        innerSec
                    } else if (angle > PI / 2) {
                        outer1.end
                    } else {
                        inner1.end
                    }

                    if (clockwise) {
                        end1 = outer1.end
                        end2 = innerSec

                        nextStart1 = outer2.start
                        nextStart2 = innerStart

                    } else {
                        end1 = innerSec
                        end2 = outer1.end

                        nextStart1 = innerStart
                        nextStart2 = outer2.start
                    }

                    // connect the intersection points according to the joint style

                    if (actualJoinStyle == JoinStyle.BEVEL) {
                        // simply connect the intersection points
                        addMeshPoint(outer1.end, travelled, isOutside = clockwise)
                        addMeshPoint(outer2.start, travelled, isOutside = clockwise)
                        addMeshPoint(innerSec, travelled, isOutside = ! clockwise)

                        if (clockwise) {
                            addInteriorPoint(outer2.start, outOfSequence = true)
                        } else {
                            addExteriorPoint(outer2.start, outOfSequence = true)
                        }

                    } else {
                        // draw a circle between the ends of the outer edges,
                        // centered at the actual point
                        // with half the line thickness as the radius
                        createTriangleFan(
                            innerSec, segment1.center.end, outer1.end, outer2.start,
                            clockwise, travelled, isEndCap = false
                        )
                    }
                }
            }
        }
    }

    // region == PolySegment ==
    private class PolySegment(val center: LineSegment, insideThickness: Float, outsideThickness: Float) {

        val midPoint = (center.start + center.end) / 2f

        // calculate the segment's outer edges by offsetting
        // the central line by the normal vector
        // multiplied with the thickness
        val edge1 = center + center.normal() * outsideThickness
        val edge2 = center - center.normal() * insideThickness

        override fun toString() = "PolySegment e1 : ${edge1}  e2 : ${edge2}"
    }
    // endregion PolySegment

    // region == LineSegment ==
    class LineSegment(var start: Vector2, var end: Vector2) {

        fun length() = (end - start).magnitude()

        fun normal() = (end - start).perpendicular().direction()

        fun direction() = (end - start)

        fun normalisedDirection() = direction().direction()

        operator fun plus(a: Vector2) = LineSegment(start + a, end + a)
        operator fun minus(a: Vector2) = LineSegment(start - a, end - a)

        override fun toString() = "LineSegment $start -> $end"

        companion object {

            fun intersection(a: LineSegment, b: LineSegment, infiniteLines: Boolean): Vector2? {
                // calculate un-normalized direction vectors
                val r = a.direction()
                val s = b.direction()

                val originDist = b.start - a.start

                val uNumerator = originDist.cross(r)
                val denominator = r.cross(s)

                if (abs(denominator) < 0.0001f) {
                    // The lines are parallel
                    return null
                }

                // solve the intersection positions
                val u = uNumerator / denominator
                val t = originDist.cross(s) / denominator

                if (! infiniteLines && (t < 0 || t > 1 || u < 0 || u > 1)) {
                    // the intersection lies outside the line segments
                    return null
                }

                // calculate the intersection point
                return a.start + r * t
            }
        }

    }
    // endregion LineSegment
}
